// ═══════════════════════════════════════════════════════════════════════════════════════════
// ChannelStateCell — ONE channel's column in the grid (אימייל | וואטסאפ), 11-sendsearch §8.
//
// It replaces "ערוצים ותוצאה" + "הראיה האחרונה": one report row is now one person in one SEND, so
// each channel gets its own column and the two can be read down the page independently.
//
// Same hard rule as SendStatusCell (CONTRACT D10): NO status dictionary, map, switch or lookup in
// this file. It receives an attempt that the model ALREADY narrowed (`rowEmailAttempt` /
// `rowWhatsAppAttempt` → `toChannelAttempt`) and builds the words by key construction:
//     t('SendSearch.delivery.' + camelCaseState(state))
// and the colour through `deliveryTone` / `engagementTone`. What the grey sub-line says (template,
// error code, not-sent reason) is decided by `waSubLineOf` in the model, not here.
//
// `attempt === null` is a FIRST-CLASS value: the row's send has no such channel ⇒ `—` (§8). It is
// NOT "nothing happened": a send that has the channel but did not reach this person arrives as a
// real state ('NoEmail', 'NoPhone', 'Duplicate', 'NotSent') and is rendered in words.
// ═══════════════════════════════════════════════════════════════════════════════════════════

import React from 'react';
import { Box, Tooltip, TooltipProps, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { DateFormats } from '../../../helpers/Constants';
import {
    SS,
    ChannelAttempt,
    StateTone,
    camelCaseState,
    deliveryTone,
    engagementTone,
} from '../../../Models/DataSources/SendSearch';

// Tone → colour. Colour only; the same tokens SendStatusCell and the drawers use.
const TONE_COLOR: { [k in StateTone]: string } = {
    ok: '#067647', bad: '#B42318', warn: '#B54708', muted: '#5b6b7b',
};

interface Props {
    attempt: ChannelAttempt | null;
    // CHANGED 2026-10-02 (Idan): the channel's campaign name, ABOVE the status — the e-mail
    // campaign in אימייל, the WhatsApp unit in וואטסאפ. Shown only when the send has the channel.
    name?: string | null;
    // The grey line under the status, already in words. Optional: the e-mail column has none.
    subLine?: string | null;
    // Hover text for the sub-line (e.g. the provider's error text behind an error code).
    subLineTitle?: string | null;
    // Optional (QA BUG-02): handed to both Tooltips. RollupDrawer passes { disablePortal: true } so the hover
    // is not hidden under the drawer; the grid passes nothing (unchanged).
    tooltipPopperProps?: TooltipProps['PopperProps'];
}

const ChannelStateCell: React.FC<Props> = ({ attempt, name, subLine, subLineTitle, tooltipPopperProps }) => {
    const { t } = useTranslation();

    if (!attempt) {
        return <Typography component="span" style={{ color: '#a8b2bb' }}>—</Typography>;
    }

    const deliveryLabel = t(`${SS}delivery.${camelCaseState(attempt.DeliveryState)}`);
    const hasEngagement = attempt.EngagementState !== 'None';
    // The strongest evidence decides the colour: a real engagement outranks the delivery state.
    const tone: StateTone = hasEngagement ? engagementTone(attempt.EngagementState) : deliveryTone(attempt.DeliveryState);
    const words = hasEngagement
        ? `${deliveryLabel} · ${t(`${SS}engagement.${camelCaseState(attempt.EngagementState)}`)}`
        : deliveryLabel;
    const when = attempt.EvidenceAt ? moment(attempt.EvidenceAt).format(DateFormats.DATE_TIME_24) : '';

    const sub = subLine
        ? (
            <Typography
                component="div"
                style={{
                    fontSize: 12, color: 'var(--pls-d-text-2, #5b6b7b)', maxWidth: 220, marginInlineStart: 'auto', marginInlineEnd: 'auto',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
            >
                {/* <bdi>: a Latin template name must not drag the Hebrew line around it. */}
                <bdi>{subLine}</bdi>
            </Typography>
        )
        : null;

    const campaignName = (name ?? '').trim();

    return (
        <Box style={{ lineHeight: 1.35 }}>
            {campaignName && (
                <Tooltip title={campaignName} PopperProps={tooltipPopperProps}>
                    <Typography
                        component="div"
                        style={{
                            fontSize: 14, fontWeight: 600, color: 'var(--pls-d-text, #3b4754)', maxWidth: 240,
                            marginInlineStart: 'auto', marginInlineEnd: 'auto',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                    >
                        <bdi>{campaignName}</bdi>
                    </Typography>
                </Tooltip>
            )}
            <Typography
                component="div"
                style={{ fontSize: 13.5, fontWeight: tone === 'muted' ? 400 : 700, color: TONE_COLOR[tone] }}
            >
                {words}
            </Typography>
            {when && (
                // `unicodeBidi: isolate` is what keeps 'DD/MM/YYYY HH:mm' in order inside an RTL cell;
                // `direction: ltr` alone on an inline run is inert.
                <Typography component="div" style={{ fontSize: 12, color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                    <span style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>{when}</span>
                </Typography>
            )}
            {sub && subLineTitle
                ? <Tooltip title={subLineTitle} PopperProps={tooltipPopperProps}>{sub}</Tooltip>
                : sub}
        </Box>
    );
};

export default ChannelStateCell;
